import React, { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyProgress() {
  const [stats, setStats] = useState(null);
  const [sosEffectiveness, setSosEffectiveness] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchSosEffectiveness();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/progress/stats`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSosEffectiveness = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/sos/effectiveness`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSosEffectiveness(data);
      }
    } catch (error) {
      console.error('Error fetching SOS effectiveness:', error);
    }
  };

  const getSkillCounts = () => {
    if (!stats?.this_week?.skills_practiced) return {};
    const counts = {};
    stats.this_week.skills_practiced.forEach(skill => {
      counts[skill] = (counts[skill] || 0) + 1;
    });
    return counts;
  };

  const getEnergyTrend = () => {
    if (!stats?.this_week?.energy_trend || stats.this_week.energy_trend.length < 2) {
      return 'stable';
    }
    const trend = stats.this_week.energy_trend;
    const firstEnergy = trend[0].energy;
    const lastEnergy = trend[trend.length - 1].energy;
    if (lastEnergy > firstEnergy + 1) return 'upward';
    if (lastEnergy < firstEnergy - 1) return 'downward';
    return 'stable';
  };

  const getEncouragingMessage = () => {
    if (!stats?.overall) return "Every small action builds capacity.";
    
    const daysEngaged = stats.overall.days_of_engagement;
    const skillsCount = stats.overall.total_skills_practiced;
    
    // High engagement: 5+ days or 10+ skills
    if (daysEngaged >= 5 || skillsCount >= 10) {
      return "You're showing up for yourself consistently!";
    }
    
    // Moderate engagement: 2-4 days or 3-9 skills
    if (daysEngaged >= 2 || skillsCount >= 3) {
      return "Every small action builds capacity.";
    }
    
    // Low engagement but present
    return "Progress isn't linear. You're here, and that matters.";
  };

  const getDayLabel = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[date.getDay()];
  };

  const getWeeklyReflection = () => {
    if (!stats) return '';
    
    const skillCounts = getSkillCounts();
    const topSkill = Object.entries(skillCounts).sort((a, b) => b[1] - a[1])[0];
    const energyTrend = getEnergyTrend();
    
    let reflection = 'This week ';
    
    if (topSkill) {
      reflection += `you practiced ${topSkill[0]} ${topSkill[1]} time${topSkill[1] > 1 ? 's' : ''}. `;
    }
    
    if (energyTrend === 'upward') {
      reflection += 'Your energy is trending upward. ';
    } else if (energyTrend === 'stable') {
      reflection += 'Your energy has been consistent. ';
    }
    
    reflection += "You're becoming who you want to be.";
    
    return reflection;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1C2127] via-[#2D3640] to-[#1C2127] pb-20">
        <Navigation />
        <main className="max-w-md mx-auto px-4 py-6">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00B4D8] mx-auto"></div>
            <p className="text-[#ADB5BD] mt-4">Loading your progress...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1C2127] via-[#2D3640] to-[#1C2127] pb-20">
      <Navigation />
      
      <main className="max-w-md mx-auto px-4 py-6 space-y-6 animate-fade-in">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight text-[#F8F9FA]" data-testid="progress-title">
            You're Building Capacity
          </h1>
          <p className="text-base text-[#ADB5BD] leading-relaxed">
            Week of {stats?.this_week?.energy_trend?.[0]?.date ? new Date(stats.this_week.energy_trend[0].date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : 'this week'}
          </p>
        </div>

        {/* This Week's Wins */}
        <div className="bg-[#2D3640] rounded-2xl border border-[#4A5568] card-shadow-soft p-6 space-y-5">
          <h2 className="text-xl font-semibold text-[#F8F9FA]">
            This Week's Wins
          </h2>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-[#3A4550] rounded-xl">
              <span className="text-sm text-[#F8F9FA]">Times you chose differently</span>
              <span className="text-xl font-bold text-[#00B4D8]" data-testid="times-chose-differently">
                {stats?.this_week?.times_chose_differently || 0}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-[#3A4550] rounded-xl">
              <span className="text-sm text-[#F8F9FA]">SOS used successfully</span>
              <span className="text-xl font-bold text-[#00B4D8]" data-testid="sos-success-count">
                {stats?.this_week?.sos_success_count || 0} times
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-[#3A4550] rounded-xl">
              <span className="text-sm text-[#F8F9FA]">Check-ins completed</span>
              <span className="text-xl font-bold text-[#00B4D8]" data-testid="checkins-completed">
                {stats?.this_week?.check_ins_count || 0} of {stats?.this_week?.possible_check_ins || 14}
              </span>
            </div>
          </div>

          {/* Skills Practiced */}
          {stats?.this_week?.skills_practiced && stats.this_week.skills_practiced.length > 0 && (
            <div>
              <h3 className="text-base font-medium text-[#F8F9FA] mb-3">Skills I practiced:</h3>
              <div className="space-y-2">
                {Object.entries(getSkillCounts()).map(([skill, count]) => (
                  <div key={skill} className="flex items-center justify-between p-3 bg-[#3A4550] rounded-lg">
                    <span className="text-sm text-[#F8F9FA]">{skill}</span>
                    <span className="text-sm font-semibold text-[#00B4D8]">{count}×</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Energy Trend */}
          {stats?.this_week?.energy_trend && stats.this_week.energy_trend.length > 0 && (
            <div>
              <h3 className="text-base font-medium text-[#F8F9FA] mb-3">Energy trend:</h3>
              <div className="relative h-40 bg-[#3A4550] rounded-xl p-4">
                <svg className="w-full h-full" viewBox="0 0 100 70" preserveAspectRatio="none">
                  {/* Grid lines */}
                  <line x1="0" y1="17.5" x2="100" y2="17.5" stroke="#E2E8F0" strokeWidth="0.5" />
                  <line x1="0" y1="35" x2="100" y2="35" stroke="#E2E8F0" strokeWidth="0.5" />
                  <line x1="0" y1="52.5" x2="100" y2="52.5" stroke="#E2E8F0" strokeWidth="0.5" />
                  
                  {/* Energy line */}
                  <polyline
                    points={stats.this_week.energy_trend.map((point, index) => {
                      const x = (index / (stats.this_week.energy_trend.length - 1 || 1)) * 100;
                      const y = 70 - (point.energy / 10) * 60;
                      return `${x},${y}`;
                    }).join(' ')}
                    fill="none"
                    stroke="#6F8C9B"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  
                  {/* Data points */}
                  {stats.this_week.energy_trend.map((point, index) => {
                    const x = (index / (stats.this_week.energy_trend.length - 1 || 1)) * 100;
                    const y = 70 - (point.energy / 10) * 60;
                    return <circle key={index} cx={x} cy={y} r="2.5" fill="#6F8C9B" />;
                  })}
                </svg>
                <div className="absolute bottom-2 left-2 right-2 flex justify-between text-xs text-[#6B7280]">
                  {stats.this_week.energy_trend.map((point, index) => (
                    <span key={index} className="flex-1 text-center">
                      {getDayLabel(point.date)}
                    </span>
                  ))}
                </div>
              </div>
              <p className="text-sm text-[#ADB5BD] mt-2 text-center">
                Your energy is trending <span className="font-semibold text-[#00B4D8]">{getEnergyTrend()}</span>
              </p>
            </div>
          )}
        </div>

        {/* Encouraging Message */}
        <div className="bg-gradient-to-r from-[#00B4D8] to-[#06D6A0] rounded-2xl p-6 text-white text-center">
          <div className="text-3xl mb-2">🌟</div>
          <p className="text-lg font-semibold mb-2">
            {getEncouragingMessage()}
          </p>
          <p className="text-sm opacity-90">
            Every practice strengthens your skills
          </p>
        </div>

        {/* Capacity Growth Visual */}
        <div className="bg-[#2D3640] rounded-2xl border border-[#4A5568] card-shadow-soft p-6 space-y-5">
          <h2 className="text-xl font-semibold text-[#F8F9FA]">
            Capacity Growth
          </h2>

          {/* Skills Practiced */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-[#F8F9FA]">Skills Practiced</span>
              <span className="text-lg font-bold text-[#00B4D8]" data-testid="total-skills">
                {stats?.overall?.total_skills_practiced || 0} times
              </span>
            </div>
            <div className="h-3 bg-[#3A4550] rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#00B4D8] to-[#06D6A0] rounded-full transition-all duration-500"
                style={{ width: `${Math.min((stats?.overall?.total_skills_practiced || 0) * 2, 100)}%` }}
              />
            </div>
          </div>

          {/* Days of Engagement */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-[#F8F9FA]">Days of Engagement</span>
              <span className="text-lg font-bold text-[#00B4D8]" data-testid="days-engaged">
                {stats?.overall?.days_of_engagement || 0} days
              </span>
            </div>
            <div className="h-3 bg-[#3A4550] rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#10B981] to-[#059669] rounded-full transition-all duration-500"
                style={{ width: `${Math.min((stats?.overall?.days_of_engagement || 0) * 3, 100)}%` }}
              />
            </div>
          </div>

          {/* Modules Completed */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-[#F8F9FA]">Modules Completed</span>
              <span className="text-lg font-bold text-[#00B4D8]" data-testid="overall-modules">
                {stats?.overall?.modules_completed || 0} of {stats?.overall?.total_modules || 14}
              </span>
            </div>
            <div className="h-3 bg-[#3A4550] rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#00B4D8] to-[#06D6A0] rounded-full transition-all duration-500"
                style={{ width: `${((stats?.overall?.modules_completed || 0) / (stats?.overall?.total_modules || 14)) * 100}%` }}
              />
            </div>
          </div>

          {/* Growing Tree Visual */}
          <div className="pt-4 text-center">
            <div className="inline-block relative">
              <div className="text-7xl">
                {stats?.overall?.modules_completed === 0 && '🌱'}
                {stats?.overall?.modules_completed >= 1 && stats?.overall?.modules_completed <= 3 && '🌿'}
                {stats?.overall?.modules_completed >= 4 && stats?.overall?.modules_completed <= 7 && '🪴'}
                {stats?.overall?.modules_completed >= 8 && stats?.overall?.modules_completed <= 11 && '🌳'}
                {stats?.overall?.modules_completed >= 12 && '🌲'}
              </div>
              <p className="text-xs text-[#ADB5BD] mt-2">
                {stats?.overall?.modules_completed === 0 && 'Just planted'}
                {stats?.overall?.modules_completed >= 1 && stats?.overall?.modules_completed <= 3 && 'Growing strong'}
                {stats?.overall?.modules_completed >= 4 && stats?.overall?.modules_completed <= 7 && 'Taking root'}
                {stats?.overall?.modules_completed >= 8 && stats?.overall?.modules_completed <= 11 && 'Flourishing'}
                {stats?.overall?.modules_completed >= 12 && 'Fully grown'}
              </p>
            </div>
          </div>
        </div>

        {/* Weekly Reflection */}
        {stats?.this_week?.check_ins_count > 0 && (
          <div className="bg-[#3A4550] rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-[#F8F9FA] mb-3">
              Weekly Reflection
            </h2>
            <p className="text-base text-[#F8F9FA] leading-relaxed italic">
              {getWeeklyReflection()}
            </p>
          </div>
        )}

        {/* Your Most Helpful Strategies */}
        {sosEffectiveness && (sosEffectiveness.show_learning_message || (sosEffectiveness.top_strategies && sosEffectiveness.top_strategies.length > 0)) && (
          <div className="bg-[#2D3640] rounded-2xl border border-[#4A5568] card-shadow-soft p-6 space-y-4">
            <div className="space-y-1">
              <h2 className="text-xl font-semibold text-[#F8F9FA]">
                Your Most Effective Strategies
              </h2>
              <p className="text-sm text-[#ADB5BD]">
                {sosEffectiveness.show_learning_message 
                  ? 'Discover what works best for you' 
                  : 'Based on what&apos;s worked for you before'}
              </p>
            </div>

            {sosEffectiveness.show_learning_message ? (
              <div className="p-6 bg-[#2D3640] rounded-xl text-center">
                <div className="text-4xl mb-3">🌱</div>
                <p className="text-base text-[#F8F9FA] leading-relaxed">
                  As you practice different coping strategies, you&apos;ll learn which work best for you. Use the SOS button when you need support, and we&apos;ll track what helps most.
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {sosEffectiveness.top_strategies.map((strategy, index) => (
                    <div key={strategy.strategy} className="flex items-start gap-4 p-4 bg-[#3A4550] rounded-xl">
                      <div className="w-8 h-8 rounded-full bg-[#00B4D8] flex items-center justify-center text-white font-semibold flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-[#F8F9FA] mb-1">
                          {strategy.label}
                        </h3>
                        <p className="text-sm text-[#ADB5BD]">
                          You&apos;ve found this helpful {strategy.helpful_count} out of {strategy.total_count} times
                          {strategy.success_rate >= 75 && ' - This works really well for you!'}
                        </p>
                        <div className="mt-2 h-2 bg-[#2D3640] rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#00B4D8] rounded-full transition-all duration-500"
                            style={{ width: `${strategy.success_rate}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pt-2 px-4 py-3 bg-[#3A4550] rounded-xl">
                  <p className="text-sm text-[#ADB5BD] italic">
                    💡 Based on your history, try <strong className="text-[#00B4D8]">{sosEffectiveness.top_strategies[0]?.label.toLowerCase()}</strong> first when you need support.
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Empty State */}
        {stats?.overall?.total_skills_practiced === 0 && (
          <div className="bg-[#2D3640] rounded-2xl border border-[#4A5568] card-shadow-soft p-8 text-center">
            <div className="w-16 h-16 bg-[#3A4550] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-[#00B4D8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-[#F8F9FA] mb-2">
              Your journey begins now
            </h3>
            <p className="text-sm text-[#ADB5BD]">
              Start with your first morning check-in or explore a module to begin building capacity
            </p>
          </div>
        )}

        {/* Bottom Message */}
        <div className="text-center py-6">
          <p className="text-sm text-[#ADB5BD] italic">
            Remember: You're measuring capacity built, not perfection achieved.
          </p>
        </div>
      </main>
    </div>
  );
}
